import { useQueryClient } from "react-query";
import { axiosInstance } from "api/axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { uploadCompanyEncryptedFile } from "utils/uploadCompanyEncryptedFile";
import { IContract, ICreateContractRequest } from "dto/Contracts/IContract";
import {
  IContractCruHistory,
  IContractCruMetric,
  IContractCruSide,
  ICruPublishResult,
} from "dto/Contracts/IContractCru";

/**
 * Rejestr umów — REST API (BE: ContractsController).
 * Plik umowy jest szyfrowany po stronie klienta kluczem obiektu (zawiniętym
 * kluczem publicznym aktywnego profilu) i wgrywany przez /file/init + /file/confirm;
 * do BE trafia tylko FileUri + KeyId.
 */

// ---------------------------------------------------------------------------
// Funkcje API
// ---------------------------------------------------------------------------

const getContractsApi = async (searchText?: string): Promise<IContract[]> =>
  (
    await axiosInstance.get<IContract[]>("/contracts", {
      params: { Text: searchText?.trim() || undefined },
    })
  ).data;

// Pojedyncza umowa po Id — to samo źródło co rejestr Umów (do metryki umowy
// na poziomie pozycji w Budżet / Lista wydatków).
const getContractApi = async (id: string): Promise<IContract> =>
  (await axiosInstance.get<IContract>(`/contracts/${id}`)).data;

const createContractApi = async (
  request: ICreateContractRequest & { FileUri?: string; KeyId?: string },
): Promise<IContract> =>
  (await axiosInstance.post<IContract>("/contracts", request)).data;

// ── Metryka CRU (port ContractRepository z Magistratu) ──────────────────────

const getCruMetricApi = async (id: string): Promise<IContractCruMetric> =>
  (await axiosInstance.get<IContractCruMetric>(`/contracts/${id}/cru`)).data;

const saveCruMetricApi = async (
  id: string,
  metric: IContractCruMetric,
): Promise<IContract> =>
  (await axiosInstance.put<IContract>(`/contracts/${id}/cru`, metric)).data;

const createCruContractApi = async (
  metric: IContractCruMetric,
): Promise<IContract> =>
  (await axiosInstance.post<IContract>("/contracts/cru", metric)).data;

const getDocumentCruMetricApi = async (
  documentId: string,
): Promise<IContractCruMetric> =>
  (
    await axiosInstance.get<IContractCruMetric>(
      `/contracts/document/${documentId}/cru`,
    )
  ).data;

const saveDocumentCruMetricApi = async (
  documentId: string,
  metric: IContractCruMetric,
): Promise<IContract> =>
  (
    await axiosInstance.put<IContract>(
      `/contracts/document/${documentId}/cru`,
      metric,
    )
  ).data;

const getParcelAttachmentCruMetricApi = async (
  parcelId: string,
  uri: string,
): Promise<IContractCruMetric> =>
  (
    await axiosInstance.get<IContractCruMetric>(
      `/contracts/parcel/${parcelId}/cru`,
      { params: { uri } },
    )
  ).data;

const saveParcelAttachmentCruMetricApi = async (
  parcelId: string,
  uri: string,
  metric: IContractCruMetric,
): Promise<IContract> =>
  (
    await axiosInstance.put<IContract>(
      `/contracts/parcel/${parcelId}/cru`,
      metric,
      { params: { uri } },
    )
  ).data;

const suggestSidesApi = async (query: string): Promise<IContractCruSide[]> =>
  (
    await axiosInstance.get<IContractCruSide[]>("/contracts/cru/sides", {
      params: { query: query || undefined },
    })
  ).data;

const removeContractApi = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/contracts/${id}`);
};

// ── CRU: publikacja / aktualizacja / wycofanie / historia ───────────────────

const publishToCruApi = async (id: string): Promise<ICruPublishResult> =>
  (await axiosInstance.post<ICruPublishResult>(`/contracts/${id}/cru/publish`))
    .data;

const updateInCruApi = async (id: string): Promise<ICruPublishResult> =>
  (await axiosInstance.post<ICruPublishResult>(`/contracts/${id}/cru/update`))
    .data;

const withdrawFromCruApi = async (
  id: string,
  comment: string,
): Promise<ICruPublishResult> =>
  (
    await axiosInstance.post<ICruPublishResult>(
      `/contracts/${id}/cru/withdraw`,
      { Comment: comment },
    )
  ).data;

const getCruHistoryApi = async (id: string): Promise<IContractCruHistory[]> =>
  (await axiosInstance.get<IContractCruHistory[]>(`/contracts/${id}/cru/history`))
    .data;

// ---------------------------------------------------------------------------
// Typy
// ---------------------------------------------------------------------------

export interface ICreateContractPayload extends ICreateContractRequest {
  file?: File | null;
}

// ---------------------------------------------------------------------------
// Klucze zapytań
// ---------------------------------------------------------------------------

const QUERY_KEY = "contracts";

// ---------------------------------------------------------------------------
// Hooki React Query
// ---------------------------------------------------------------------------

const invalidateContracts = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries([QUERY_KEY]);
  // węzeł "Umowy" w drzewie wydatków budżetu (budgetService.queryKeys.contracts)
  queryClient.invalidateQueries("budget.contracts");
};

const contractsService = {
  getCruMetric: getCruMetricApi,
  getDocumentCruMetric: getDocumentCruMetricApi,
  getParcelAttachmentCruMetric: getParcelAttachmentCruMetricApi,
  suggestSides: suggestSidesApi,
  getCruHistory: getCruHistoryApi,

  useGetContracts: (searchText?: string) =>
    useCustomQuery<IContract[]>(
      [QUERY_KEY, searchText ?? ""],
      () => getContractsApi(searchText),
      { retry: false },
    ),

  useGetContract: (id: string, enabled = true) =>
    useCustomQuery<IContract>(
      [QUERY_KEY, "byId", id],
      () => getContractApi(id),
      { enabled: enabled && !!id, retry: false },
    ),

  useCreateContract: () => {
    const queryClient = useQueryClient();

    return useCustomMutation<IContract, ICreateContractPayload>(
      async (payload) => {
        const { file, ...request } = payload;

        let fileFields: { FileUri: string; KeyId: string } | undefined;
        if (file) {
          // Skan dokumentu rejestrowego — klucz obiektu owinięty kluczem FIRMY
          // (docs/model-kluczy-firmowych.md, I1), nie kluczem aktywnego profilu.
          fileFields = await uploadCompanyEncryptedFile(file);
        }

        return createContractApi({ ...request, ...fileFields });
      },
      {
        onSuccess: () => invalidateContracts(queryClient),
      },
    );
  },

  /** Nowa umowa w formacie CRU (pełna metryka, walidacja jak w kreatorze Magistratu). */
  useCreateCruContract: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<IContract, IContractCruMetric>(createCruContractApi, {
      onSuccess: () => invalidateContracts(queryClient),
    });
  },

  useSaveCruMetric: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<IContract, { id: string; metric: IContractCruMetric }>(
      ({ id, metric }) => saveCruMetricApi(id, metric),
      { onSuccess: () => invalidateContracts(queryClient) },
    );
  },

  useSaveDocumentCruMetric: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<
      IContract,
      { documentId: string; metric: IContractCruMetric }
    >(({ documentId, metric }) => saveDocumentCruMetricApi(documentId, metric), {
      onSuccess: () => invalidateContracts(queryClient),
    });
  },

  useSaveParcelAttachmentCruMetric: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<
      IContract,
      { parcelId: string; uri: string; metric: IContractCruMetric }
    >(
      ({ parcelId, uri, metric }) =>
        saveParcelAttachmentCruMetricApi(parcelId, uri, metric),
      { onSuccess: () => invalidateContracts(queryClient) },
    );
  },

  useRemoveContract: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<void, string>(removeContractApi, {
      onSuccess: () => invalidateContracts(queryClient),
    });
  },

  usePublishToCru: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<ICruPublishResult, string>(publishToCruApi, {
      onSuccess: () => invalidateContracts(queryClient),
    });
  },

  useUpdateInCru: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<ICruPublishResult, string>(updateInCruApi, {
      onSuccess: () => invalidateContracts(queryClient),
    });
  },

  useWithdrawFromCru: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<ICruPublishResult, { id: string; comment: string }>(
      ({ id, comment }) => withdrawFromCruApi(id, comment),
      { onSuccess: () => invalidateContracts(queryClient) },
    );
  },
};

export default contractsService;
